import { Tooltip } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import { EditIcon } from '@mm/icons';
import { useWidgetCRUDActions } from '@mm/state/actions/WidgetCRUDActions';
import ButtonModel from '@mm/state/model/ButtonModel';
import { PlatformType } from '@mm/state/model/CampaignsModel';
import WidgetModel, {
  FollowUpType,
  QuickQuestionWidgetConfig,
  WidgetTypes,
} from '@mm/state/model/WidgetModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { isAnyModalOpen } from '@mm/state/selectors/modalSelector';
import { pageEntityByIdSelector } from '@mm/state/selectors/pageSelectors';
import classNames from 'classnames';
import { debounce } from 'lodash';
import moment from 'moment';
import pluralize from 'pluralize';
import React, { useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import useRouter from 'use-react-router';
import { colors } from '../../../../editor/features/beta/styles/colors';
import { BotBuilderRoutes } from '../../../../routes/bot-builder-routes';
import { errorToast, successToast } from '../../../helpers/toast';
import { useCommonWidgetHandlers } from '../../../helpers/widget-helpers/use-common-widget-handlers';
import { ActivePathSelection } from '../../common/ActivePathSelection/ActivePathSelection';
import { BubbleTextInput } from '../../common/BubbleTextInput/BubbleTextInput';
import { TimePeriodSelector } from '../../DelayWidget/TimePeriodSelector/TimePeriodSelector';
import { PathButton } from '../QuestionBotTextArea/QuestionButton/QuestionButton';
import css from './QuestionReminder.module.css';

type TimePeriodCompProps = React.ComponentProps<typeof TimePeriodSelector>;

type Props = {
  chatbotId: number;
  widget: WidgetModel;
  className?: string;
  disabled?: boolean;
  widgetsToExclude?: WidgetTypes[];
  platforms?: PlatformType[];
  usedInSlideout?: boolean;
};
// & Pick<
//   InputHTMLAttributes<HTMLInputElement>,
//   'checked' | 'disabled' | 'onChange'
// >;

const getDefaultValues = (s?: string): [string, number] => {
  switch (s) {
    case 'minute':
      return ['minute', 10];
    case 'hour':
      return ['hour', 1];
    default:
      return ['hour', 1];
  }
};

const getMax = measure => moment.duration(23, 'hour').as(measure);

const getFormattedTime = (value: number, measure: string) => {
  switch (measure) {
    case 'minute':
      return `${value} ${pluralize('minute', value)}`;
    case 'hour':
      return `${value} ${pluralize('hour', value)}`;
    default:
      return value;
  }
};

const getFormattedAction = (id: FollowUpType) => {
  switch (id) {
    case FollowUpType.reminder:
      return 'send a reminder';
    case FollowUpType.page:
      return 'follow up with dialogue:';
    default:
      return '';
  }
};

const actions = [
  { id: FollowUpType.reminder, name: 'Send a reminder' },
  { id: FollowUpType.page, name: 'Choose Dialogue' },
];

const debouncer = debounce((fn, args) => fn(...args), 700);

const isFollowUpException = (err: any) => {
  return (
    err?.error_code === '1313' ||
    (Array.isArray(err) &&
      err?.some(e => e.toString().toLowerCase().includes('1313')))
  );
};

export const QuestionReminder = ({
  className,
  widget,
  disabled = false,
  chatbotId,
  widgetsToExclude,
  platforms,
  usedInSlideout,
}: Props) => {
  const config = widget.config as QuickQuestionWidgetConfig;

  const {
    followUpType: type,
    followUpTimeoutMeasure,
    followUpTimeout,
    followUpPageId,
    followUpText,
  } = config;

  const id = followUpPageId?.toString() || '';

  const dialogueName =
    useSelector(state => pageEntityByIdSelector(state, id))?.name || '';

  const isModalOpen = useSelector(isAnyModalOpen);
  const qqFollowUpsFeature = useSelector(
    combinedFeatureFlagsSelector,
  ).qqFollowUps;

  const [path, setPath] = useState(false);

  const commonWidgetHandlers = useCommonWidgetHandlers();
  const widgetCRUDActions = useWidgetCRUDActions();
  const modalActions = useModalActions();

  const { history } = useRouter();

  const updateWidgetConfig = async (
    partial: Partial<QuickQuestionWidgetConfig>,
  ) => {
    try {
      await commonWidgetHandlers.update({
        ...widget,
        config: {
          ...config,
          ...partial,
        },
      });
      successToast('Widget updated');
    } catch (err) {
      errorToast("Couldn't update widget");

      if (isFollowUpException(err)) {
        modalActions.openGeneralConfirmModal({
          headerText: 'Automatic Follow Up is ON',
          messageText:
            'You need to disable automatic follow up first to make widget work properly',
          okButtonText: 'Disable',
          onCloseHandler: ok => {
            if (ok && chatbotId && history) {
              history.push(
                BotBuilderRoutes.automationRules.getLink({
                  chatbotId,
                  kind: 'follow_ups',
                }),
              );
            }
          },
        });
      }

      throw err;
    }
  };

  const ref = useRef<typeof debouncer>(debouncer);
  const updateWidgetConfigThrottled = (...args) =>
    ref.current(updateWidgetConfig, args);

  const measure = followUpTimeoutMeasure || 'minute';
  const timeout = followUpTimeout || getDefaultValues(measure)[1];

  const checked = !!type;

  const [reminder, setReminder] = useState(followUpText || '');

  const action = useMemo(
    () => actions.find(v => v.id === type) || actions[0],
    [type],
  );

  const timeFormatted = useMemo(
    () => getFormattedTime(timeout, measure),
    [timeout, measure],
  );

  const actionFormatted = useMemo(
    () => getFormattedAction(action.id),
    [action],
  );

  const onMeasureChange: TimePeriodCompProps['onMeasureChange'] = opt => {
    updateWidgetConfig({
      followUpTimeout: !timeout
        ? getDefaultValues(opt.id.toString())
        : timeout > getMax(opt.id)
        ? getMax(opt.id)
        : timeout,
      followUpTimeoutMeasure: opt.id,
    } as Partial<QuickQuestionWidgetConfig>);
  };

  const onTimeoutChange = (followUpTimeout: number) => {
    updateWidgetConfigThrottled({ followUpTimeout });
  };

  const onReminderBlur = (v?: string) => {
    if (v !== followUpText) {
      updateWidgetConfig({ followUpText: v });
    }
  };

  const openDialogueModal = () => {
    modalActions.openDialoguePickerNewModal({
      selectedDialogueId: config.followUpPageId!,
      withNoOption: false,
      onDialogueSelected: (_, dialogue) => {
        const id = dialogue?.id === -1 ? null : dialogue?.id;

        updateWidgetConfig({
          followUpType: FollowUpType.page,
          followUpTimeout: followUpTimeout || getDefaultValues(measure)[1],
          followUpPageId: id,
        });

        if (id) {
          onPathButtonClick(id);
        }
      },
    });
  };

  const onActionChange = (followUpType: FollowUpType) => {
    if (followUpType === config.followUpType) {
      return;
    }

    switch (followUpType) {
      case FollowUpType.reminder: {
        return updateWidgetConfig({ followUpType });
      }
      case FollowUpType.page:
        return openDialogueModal();
    }
  };

  const onCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!qqFollowUpsFeature && e.target.checked) {
      showUpgradeModal();
    } else {
      const update = {
        followUpType: e.target.checked ? action.id : null,
        followUpTimeout: followUpTimeout || getDefaultValues(measure)[1],
        followUpTimeoutMeasure: measure,
      } as Partial<QuickQuestionWidgetConfig>;

      updateWidgetConfig(update);
    }
  };

  const onClickOutside = (_, closePopup: Function) => {
    if (!isModalOpen) {
      closePopup();
    }
  };

  const onPathButtonClick = (dialogueId: number) => {
    if (dialogueId) {
      setPath(true);
      widgetCRUDActions.retrieveAll(dialogueId, true);
    }
  };

  const showUpgradeModal = () => {
    modalActions.openUniversalUpgradeLandmineModal({
      feature: 'qqFollowUps',
      landmineName: `${landmineNames.QQ_FOLLOW_UPS}`,
    });
  };

  return (
    <div
      className={classNames(className, css.container, { [css.pathed]: path })}
    >
      <div className={css.field}>
        <input
          type="checkbox"
          className={css.checkbox}
          disabled={disabled}
          checked={checked}
          onChange={onCheckboxChange}
        />

        <span className={css.label}>
          If user does not respond after&nbsp;
          <span className={css.time}>
            <Tooltip
              trigger="click"
              getPopupContainer={triggerNode =>
                triggerNode.parentElement || document.body
              }
              isWhite
              title={
                <TimePeriodSelector
                  className={css.period}
                  measure={measure}
                  value={timeout}
                  onMeasureChange={
                    qqFollowUpsFeature ? onMeasureChange : showUpgradeModal
                  }
                  onValueChange={
                    qqFollowUpsFeature ? onTimeoutChange : showUpgradeModal
                  }
                />
              }
            >
              <span>{timeFormatted}</span>
            </Tooltip>
          </span>
          &nbsp;then&nbsp;
          <span className={css.action}>
            <Tooltip
              trigger="click"
              isWhite
              getPopupContainer={triggerNode =>
                triggerNode.parentElement || document.body
              }
              title={() => (
                <div className={css.options}>
                  {actions.map((v, idx) => (
                    <div
                      className={classNames(css.option, {
                        [css.selected]: action?.id === v?.id,
                      })}
                      key={v?.id || idx}
                      onClick={() => {
                        if (!qqFollowUpsFeature) {
                          showUpgradeModal();
                        }
                        if (v?.id && qqFollowUpsFeature) {
                          onActionChange(v.id);
                        }
                      }}
                    >
                      <div className={css.text}>{v?.name}</div>
                    </div>
                  ))}
                </div>
              )}
            >
              <span>{actionFormatted}</span>
            </Tooltip>
          </span>
          {action.id === FollowUpType.page && (
            <span className={css.name}>
              <span onClick={openDialogueModal}>{dialogueName}</span>

              <div className={css.icons}>
                <EditIcon onClick={openDialogueModal} />
                <PathButton
                  onClick={() => {
                    if (followUpPageId) {
                      onPathButtonClick(followUpPageId);
                    }
                  }}
                  active={path}
                />
              </div>
            </span>
          )}
        </span>
      </div>

      {checked && action.id === FollowUpType.reminder && (
        <div className={css.reminder}>
          <BubbleTextInput
            value={reminder}
            editMode
            maxLength={500}
            emojiPickerColor={colors.gray}
            attributePickerColor={colors.gray}
            onClick={() => {}}
            onBlur={onReminderBlur}
            updateText={setReminder}
          />
        </div>
      )}

      {checked && action.id === FollowUpType.page && path && chatbotId && (
        <span className={css.path}>
          <ActivePathSelection
            chatbotId={chatbotId}
            widget={widget}
            isActive
            name={dialogueName}
            closeExtraActions={() => setPath(false)}
            activeButton={
              {
                id: 'reminder',
                config: { pageId: followUpPageId },
              } as any as ButtonModel
            }
            widgetsToExclude={widgetsToExclude}
            platforms={platforms}
            usedInSlideout={usedInSlideout}
          />
        </span>
      )}
    </div>
  );
};
